import React, { useEffect, useRef, useState } from "react";
import QuizService from "../../services/QuizService";
import { useFormik } from "formik";
import toast from "react-hot-toast";

import "./css/Quiz.css";
import { QuizResultsPage } from "./component/QuizResultsPage";
import { OPTION_KEYS, OPTION_LETTERS } from "../../utilis/quizconstant";
import { Card, CardBody } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { generateCertificatePDFBlob } from "../../common/Certificate/Certificate";

const storageKey = (slug) => `quiz_progress_${slug}`;

const getLocalAnswers = (slug) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(slug)) || "{}");
  } catch {
    return {};
  }
};

const setLocalAnswer = (slug, questionId, answer) => {
  const current = getLocalAnswers(slug);
  current[String(questionId)] = answer;
  localStorage.setItem(storageKey(slug), JSON.stringify(current));
};

// ─── Component ────────────────────────────────────────────────────────────────
const Quiz = ({ quizSlug, id, courseName }) => {
  console.log("🚀 ~ Quiz ~ courseName:", courseName);
  const [detail, setDetail] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(() => {
    try {
      const saved = localStorage.getItem(`quiz_current_${quizSlug}`);
      return saved !== null ? Number(saved) : 0;
    } catch {
      return 0;
    }
  });
  const [showResults, setShowResults] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [draft, setDraft] = useState(null);
  // Ref to suppress navigation when draft is re-fetched after a save-as-draft
  const isDraftRefetchRef = useRef(false);
  // Captured at submit-time so allQuizes is guaranteed loaded
  const [submittedAsLastQuiz, setSubmittedAsLastQuiz] = useState(false);
  const [sakyo, setSakyo] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // true  = certificate email sent for the first time (HTTP 200)
  // false = already certified (non-200) or certificate not applicable
  const [firstMail, setFirstMail] = useState(false);
  // true while submitCertificate is in-flight
  const [isCertLoading, setIsCertLoading] = useState(false);
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    setSakyo(!sakyo);
  };
  // ── Fetch quiz detail ──────────────────────────────────────────────────────
  const fetchDetail = async () => {
    try {
      const response = await QuizService.getbyslug(quizSlug);
      setDetail(response);
    } catch (err) {
      console.error("Failed to load quiz:", err);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [quizSlug]);

  // ── All quizzes for this course (used for isLastQuiz & completion check) ───
  const { data: allQuizes } = useQuery({
    queryKey: ["allQuizes"],
    queryFn: async () => QuizService.getQuizById(id),
    enabled: !!id,
  });

  // ── Fetch existing draft ───────────────────────────────────────────────────
  useEffect(() => {
    const fetchDraft = async () => {
      try {
        const res = await QuizService.draftGet(quizSlug);
        // Use empty object (not null) on missing/empty draft so the
        // navigation effect below still fires even when there is no draft.
        setDraft(res ?? {});
      } catch (err) {
        console.error("Failed to fetch draft:", err);
        // Set to empty object so the navigation effect still runs
        // and can restore position from localStorage.
        setDraft({});
      }
    };
    if (detail) fetchDraft();
  }, [detail]);

  // ── On draft load: pre-select answers directly via formik & navigate ────────
  useEffect(() => {
    if (!detail || !draft) return;

    // Build answer map from API draft (text-match: dq.id is draft record ID)
    const draftAnswerMap = {};
    (draft.questions ?? []).forEach((dq) => {
      const q = detail.quizDetail.find((d) => d.questionName === dq.question);
      if (q && dq.answer) {
        draftAnswerMap[q.id] = dq.answer;
      }
    });

    // Pre-select formik values directly from API — no localStorage write needed
    if (Object.keys(draftAnswerMap).length > 0) {
      formik.setValues((prev) => ({ ...prev, ...draftAnswerMap }));
    }

    // Skip navigation when triggered by a save-as-draft re-fetch
    if (isDraftRefetchRef.current) {
      isDraftRefetchRef.current = false;
      return;
    }

    // Navigate to first unanswered — check both draft answers and localStorage
    const localAnswers = getLocalAnswers(quizSlug);
    const firstUnansweredIdx = detail.quizDetail.findIndex(
      (q) => !draftAnswerMap[q.id] && !localAnswers[String(q.id)],
    );
    setCurrentQuestion(
      firstUnansweredIdx !== -1
        ? firstUnansweredIdx
        : detail.quizDetail.length - 1,
    );
  }, [detail, draft]);

  // ── Formik — single instance for all answer state ─────────────────────────
  // initialValues reads only from localStorage (mid-quiz Next-button saves).
  // Draft answers are applied via formik.setValues() in the effect above.
  const initialValues =
    detail?.quizDetail?.reduce((acc, q) => {
      const localAnswers = getLocalAnswers(quizSlug);
      acc[q.id] = localAnswers[String(q.id)] || "";
      return acc;
    }, {}) || {};

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    onSubmit: async (values) => {
      const payload = {
        slug: detail.slug,
        answers: Object.keys(values).map((qid) => ({
          questionId: Number(qid),
          selectedAnswer: values[qid] || null,
        })),
      };

      try {
        const response = await QuizService.submit(payload);

        // ── Persist this quiz's score to localStorage ────────────────────
        try {
          localStorage.setItem(
            `quiz_score_${quizSlug}`,
            String(response.scorePercentage ?? 0),
          );
        } catch { }

        // ── Capture isLastQuiz NOW (allQuizes is guaranteed loaded here) ─
        const isLastQuiz = allQuizes?.length
          ? allQuizes[allQuizes.length - 1].slug === quizSlug
          : false;
        setSubmittedAsLastQuiz(isLastQuiz);

        setResponseData(response);
        setShowResults(true);
        toast.success("Quiz submitted successfully!");

        // ── Completion check across all quizzes ──────────────────────────
        // A quiz is considered "done" when quiz_score_ exists in localStorage
        // (written above on every Submit). This is reliable even when
        // quiz_progress_ was wiped by Save as Draft.
        if (allQuizes?.length) {
          const allSubmitted = allQuizes.every(
            (quiz) => localStorage.getItem(`quiz_score_${quiz.slug}`) !== null,
          );

          if (allSubmitted) {
            // Check if all quiz scores are >= 60%
            const allAbove60 = allQuizes.every((quiz) => {
              const score = Number(localStorage.getItem(`quiz_score_${quiz.slug}`));
              return score >= 60;
            });

            // setTimeout(() => {
            //   toast.success("🎉 You have completed all quizzes!", {
            //     duration: 5000,
            //   });
            // }, 1200);
            setIsModalOpen(true);
            setSakyo(true);

            // Only generate certificate if all scores are above 60%
            if (allAbove60) {
              handleSubmitCertificate();
            }
          }
        }
      } catch (error) {
        console.error("Submission failed:", error);
        toast.error("Failed to submit quiz");
      }
    },
  });

  const handleSubmitCertificate = async () => {
    setIsCertLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const userName = user?.fullName || "Graduate";
      const userId = user?.id;

      const pdfBlob = await generateCertificatePDFBlob({
        recipient: userName,
        course: courseName,
      });

      if (!pdfBlob || pdfBlob.size < 1000) {
        console.error("[cert] PDF generation failed — blob too small");
        setFirstMail(false);
        setIsCertLoading(false);
        return;
      }

      const fileName = `certificate-${userName.replace(/\s+/g, "-").toLowerCase()}.pdf`;

      // ✅ Re-wrap blob with explicit PDF MIME type
      const pdfFile = new File([pdfBlob], fileName, {
        type: "application/pdf",
      });
      console.log("🚀 ~ handleSubmitCertificate ~ pdfFile:", pdfFile);

      const formData = new FormData();

      formData.append("UserId", userId);
      formData.append("CourseId", id);
      formData.append("CertificateFile", pdfFile);
      console.log("🚀 ~ handleSubmitCertificate ~ formData:", formData);

      const certResponse = await QuizService.submitCertificate(formData);
      console.log("🚀 ~ handleSubmitCertificate ~ certResponse:", certResponse);

      // success: true OR status: 200 → certificate sent for the first time  → show Generate PDF
      // otherwise                   → already certified                    → show "already certified"
      setFirstMail(certResponse?.success === true || certResponse?.status === 200);
    } catch (error) {
      console.error("Certificate submission failed:", error);
      setFirstMail(false);
    } finally {
      setIsCertLoading(false);
    }
  };

  const saveAllAsDraft = async () => {
    // Build payload from formik values (in-memory source of truth for current session)
    const payload = {
      quizName: detail.quizName,
      slug: detail.slug,
      quizDetail: detail.quizDetail.map((q) => ({
        question: q.questionName,
        answer: formik.values[q.id] || "",
      })),
    };

    // ── Step 1: Save draft to server ──────────────────────────────────────────
    try {
      await QuizService.draft(payload);
    } catch (error) {
      console.error("Draft save failed:", error);
      toast.error("Failed to save progress");
      return; // Stop here — localStorage is untouched, nothing lost
    }

    // Draft POST succeeded


    // ── Step 2: Wipe quiz progress from localStorage ─────────────────────────
    localStorage.removeItem(storageKey(quizSlug));

    // ── Step 3: Fetch saved draft and pre-select answers directly via formik ──
    try {
      const freshDraft = await QuizService.draftGet(quizSlug);

      // Build formik values from API response.
      // Use text-match (dq.question === q.questionName) because dq.id is the
      // draft record's own auto-increment ID, not the quiz question ID.
      const restoredValues = {};
      (freshDraft?.questions ?? []).forEach((dq) => {
        const q = detail.quizDetail.find((d) => d.questionName === dq.question);
        if (q && dq.answer) {
          restoredValues[q.id] = dq.answer;
        }
      });

      // Set formik values directly — no localStorage involvement needed
      formik.setValues((prev) => ({ ...prev, ...restoredValues }));

      // Keep draft state current and suppress navigation in the sync useEffect
      isDraftRefetchRef.current = true;
      setDraft(freshDraft);
    } catch (err) {
      // draftGet failed — formik already holds the user's in-session answers
      console.error("Failed to re-fetch draft after saving:", err);
    }
  };

  // ─── Early returns ─────────────────────────────────────────────────────────
  if (!detail)
    return (
      <div className="quiz-bg">
        <div className="spinner-border text-light" role="status"></div>
      </div>
    );

  if (showResults && responseData)
    return (
      <QuizResultsPage
        responseData={responseData}
        detail={detail}
        id={id}
        isLastQuiz={submittedAsLastQuiz}
        allQuizes={allQuizes}
        sakyo={sakyo}
        firstMail={firstMail}
        isCertLoading={isCertLoading}
        onRestart={async () => {
          // 1. Remove this quiz's stored progress from localStorage
          localStorage.removeItem(storageKey(quizSlug));
          localStorage.removeItem(`quiz_current_${quizSlug}`);

          // 2. Hit the retake API
          try {
            await QuizService.retakeQuiz(quizSlug);
          } catch (err) {
            console.error("Retake API failed:", err);
          }

          // 3. Reset UI
          setShowResults(false);
          setCurrentQuestion(0);
          formik.resetForm();
        }}
        courseName={courseName}
        isModalOpen={isModalOpen}
        toggleModal={toggleModal}
      />
    );

  const question = detail.quizDetail[currentQuestion];
  const isLastQuestion = currentQuestion === detail.quizDetail.length - 1;
  const totalQ = detail.quizDetail.length;
  const isAnswered = !!formik.values[question.id];

  return (
    <div className="quiz-bg">
      <div className="quiz-card">
        <div className="quiz-top-bar"></div>
        <div className="">
          <Card>
            <CardBody className="p-4">
              <div className="quiz-counter-num mb-3">
                Question {currentQuestion + 1} of {totalQ}
              </div>

              <h5
                className="fw-semibold mb-4"
                style={{ color: "rgb(123, 31, 162)" }}
              >
                {question.questionName}
              </h5>

              <form onSubmit={(e) => e.preventDefault()}>
                <div className="d-flex flex-column gap-2 mb-4">
                  {OPTION_KEYS.map((opt, idx) => {
                    const optVal = question[opt];
                    if (!optVal) return null;
                    const selected = formik.values[question.id] === optVal;
                    return (
                      <label
                        key={opt}
                        className={`quiz-option${selected ? " selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name={question.id.toString()}
                          value={optVal}
                          onChange={formik.handleChange}
                          checked={formik.values[question.id] === optVal}
                        />
                        <div className="quiz-radio-ring">
                          <div className="quiz-radio-dot" />
                        </div>
                        <span className="quiz-option-letter">
                          {OPTION_LETTERS[idx]}
                        </span>
                        <span className="quiz-option-text">{optVal}</span>
                      </label>
                    );
                  })}
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  {/* ── Prev button ── */}
                  {currentQuestion > 0 ? (
                    <button
                      type="button"
                      className="btn px-4 py-2 "
                      style={{
                        backgroundColor: "white",
                        color: "#000",
                        border: "none",
                        boxShadow: "0 0 0 0.4px #000",
                        transition: "background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#7B1FA2";
                        e.currentTarget.style.color = "#fff";
                        e.currentTarget.style.boxShadow = "0 0 0 0.4px #7B1FA2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.color = "#000";
                        e.currentTarget.style.boxShadow = "0 0 0 0.4px #000";
                      }}
                      onClick={() => setCurrentQuestion((p) => p - 1)}
                    >
                      ← Prev
                    </button>
                  ) : (
                    <div />
                  )}

                  {isLastQuestion ? (
                    /* ── Last question: Save as Draft + Submit ── */
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn px-4 py-2 "
                        style={{
                          backgroundColor: "#7B1FA2",
                          color: "#fff",
                          border: "none",
                          boxShadow: "0 0 0 0.4px #000",
                          transition: "background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#680c90ff";
                          e.currentTarget.style.color = "#fff";
                          e.currentTarget.style.boxShadow = "0 0 0 0.4px #7B1FA2";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#7B1FA2";
                          e.currentTarget.style.color = "#fff";
                          e.currentTarget.style.boxShadow = "0 0 0 0.4px #000";
                        }}
                        onClick={saveAllAsDraft}
                      >
                        Save as Draft
                      </button>

                      <button
                        type="button"
                        className="btn px-4 py-2 "
                        style={{
                          backgroundColor: "white",
                          color: "#000",
                          border: "none",
                          boxShadow: "0 0 0 0.4px #000",
                          transition: "background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#7B1FA2";
                          e.currentTarget.style.color = "#fff";
                          e.currentTarget.style.boxShadow = "0 0 0 0.4px #7B1FA2";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                          e.currentTarget.style.color = "#000";
                          e.currentTarget.style.boxShadow = "0 0 0 0.4px #000";
                        }}
                        onClick={async () => {
                          if (!isAnswered) {
                            toast.error("Please select an option.");
                            return;
                          }
                          await saveAllAsDraft();
                          formik.handleSubmit();
                        }}
                      >
                        Submit
                      </button>
                    </div>
                  ) : (
                    /* ── Next button: saves latest answer to localStorage ── */
                    <button
                      type="button"
                      className="btn px-4 py-2 "
                      style={{
                        backgroundColor: "white",
                        color: "#000",
                        border: "none",
                        boxShadow: "0 0 0 0.4px #000",
                        transition: "background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#7B1FA2";
                        e.currentTarget.style.color = "#fff";
                        e.currentTarget.style.boxShadow = "0 0 0 0.4px #7B1FA2";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "white";
                        e.currentTarget.style.color = "#000";
                        e.currentTarget.style.boxShadow = "0 0 0 0.4px #000";
                      }}
                      onClick={() => {
                        if (!isAnswered) {
                          toast.error("Please select an option before proceeding.");
                          return;
                        }
                        setLocalAnswer(quizSlug, question.id, formik.values[question.id]);
                        const nextIdx = currentQuestion + 1;
                        try {
                          localStorage.setItem(`quiz_current_${quizSlug}`, String(nextIdx));
                        } catch { }
                        setCurrentQuestion(nextIdx);
                      }}
                    >
                      Next →
                    </button>
                  )}
                </div>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
